import React, { useCallback } from "react";

import cx from "classnames";
import { noop } from "lodash";

import styles from "./css/RadioInput.module.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isRadioValue(value: any): value is string {
	return typeof value === "string";
}

export function Radio({ className, ...props }: Omit<React.HTMLProps<HTMLInputElement>, "type">) {
	return (
		<input
			type="radio"
			className={cx(styles.radioInput, className, {
				[styles.readonly]: props.readOnly,
			})}
			{...props}
		/>
	);
}

export interface RadioButtonProps<T extends string> {
	className?: string;
	name: string;
	value: T;
	selectedValue?: T | undefined;
	label: React.ReactNode;
	readonly?: boolean;
	disabled?: boolean;
	handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onChange?: (value: T) => void;
}

function RadioButton<T extends string>({
	label,
	name,
	disabled,
	readonly,
	handleChange,
	onChange,
	selectedValue,
	value,
	className,
}: RadioButtonProps<T>) {
	const checked = selectedValue !== undefined && selectedValue === value;

	const onOptionClick = useCallback(() => {
		if (checked && onChange) onChange("" as T);
	}, [checked, onChange]);

	return (
		<label className={cx(styles.radioItem, className)}>
			<Radio
				name={name}
				disabled={disabled || readonly}
				value={value}
				checked={checked}
				onChange={handleChange}
				onClick={onOptionClick}
				readOnly={readonly}
			/>
			<div className={styles.radioItemContent}>{label}</div>
		</label>
	);
}

export interface RadioOption<T extends string> {
	value: T;
	label: React.ReactNode;
	disabled?: boolean;
}

export interface RadioGroupProps<T extends string> {
	name: string;
	value: T | undefined;
	options: RadioOption<T>[];
	className?: string;
	readonly?: boolean;
	disabled?: boolean;
	onChange?: (value: T) => void;
}

/** @deprecated Use ~/ui/RadioGroup instead */
export default function RadioInput<T extends string>({
	className,
	options,
	onChange,
	readonly,
	...props
}: RadioGroupProps<T>) {
	let handleChange = noop;
	if (onChange && !readonly) {
		handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
			const value = e.target.value as T;
			onChange(value);
		};
	}
	return (
		<div className={className}>
			{options.map(({ value, label, disabled: optionDisabled }) => (
				<RadioButton
					key={value}
					name={props.name}
					handleChange={handleChange}
					onChange={onChange}
					selectedValue={props.value}
					label={label}
					value={value}
					readonly={!!readonly}
					disabled={!!optionDisabled || !!props.disabled}
				/>
			))}
		</div>
	);
}
